import React, { useCallback, useEffect, useState } from "react";
import { X, RefreshCw, Download } from "lucide-react";
import apiHelper from "../apiHelper";
import downloadXlsx from "../utils/downloadXlsx";

// ERP-5254 — the banner on Platform Listing after an Excel import is saved.
// Three states (Figma "Progress" / "All Success" / "With issues"), each in two
// halves:
//
//   left   "Creating Listing Mapping"      create / update / delete rows per platform
//   right  "Syncing Stock Qty to Platforms" stock pushes — Shopee / Lazada / TikTok
//                                          only (WooCommerce has no stock API)
//
//   pushing  amber   spinner, per-platform done / total bars
//   done ok  green   ✓
//   done bad red     !, per-platform Success / Failed, Try Again with the attempts
//                    left (3 in all; button + note hidden once none remain), ✕
//
// State comes from GET sales/listing/upload-batch/active/ (the newest saved batch
// not yet dismissed), polled every 3 s while pushing — same mechanics as the
// pricing banner (PricingSyncBanner.js).

const POLL_MS = 3000;
const num = (n) => (n == null ? "0" : Number(n).toLocaleString("en-SG"));

const THEME = {
  pushing: { bg: "#FFF8E1", border: "#E0A800", title: "#212529", bar: "#CC9A06", text: "#6C757D" },
  ok: { bg: "#D1E7DD", border: "#198754", title: "#212529", bar: "#198754", text: "#6C757D" },
  bad: { bg: "#F8D7DA", border: "#DC3545", title: "#212529", bar: "#DC3545", text: "#6C757D" },
};
const GREEN = "#146C43", RED = "#B02A37", GREY = "#6C757D", BLACK = "#212529", LINE = "#ADB5BD";

function Dot({ color }) {
  return <span style={{ display: "inline-block", width: 8, height: 8, borderRadius: 99, background: color, marginRight: 6 }} />;
}

function Bar({ done, total, color }) {
  const pct = total ? Math.min(100, Math.round((done / total) * 100)) : 100;
  return (
    <span style={{ display: "inline-block", width: 70, height: 8, borderRadius: 99, background: "#DEE2E6", overflow: "hidden", verticalAlign: "middle" }}>
      <span style={{ display: "block", width: `${pct}%`, height: "100%", background: color, transition: "width .4s ease" }} />
    </span>
  );
}

function StatusIcon({ mode, t }) {
  if (mode === "pushing") {
    return <span style={{ width: 24, height: 24, borderRadius: "50%", border: "3px solid #E9ECEF", borderTopColor: t.bar, animation: "spin 0.9s linear infinite", display: "inline-block", flexShrink: 0, boxSizing: "border-box" }} />;
  }
  // Figma: a 24px outlined circle, as tall as the title + subtitle pair next to it
  const color = mode === "ok" ? GREEN : RED;
  return (
    <span style={{ width: 24, height: 24, borderRadius: "50%", border: `2px solid ${color}`, display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 13, lineHeight: 1, fontWeight: 700, color, flexShrink: 0, boxSizing: "border-box" }}>
      {mode === "ok" ? "✓" : "!"}
    </span>
  );
}

// One half's title block (icon + title + explanation) and its platform grid
// (two columns: Shopee / Lazada, then TikTok / WooCommerce). Figma draws the
// two title blocks on one row, a full-width hairline, then the two grids side by
// side - so they are rendered as two separate rows by the banner, not stacked.
function halfMode(mode, failed) {
  return mode === "pushing" ? "pushing" : failed > 0 ? "bad" : "ok";
}

function HalfTitle({ mode, t, title, subtitle, failed }) {
  const hm = halfMode(mode, failed);
  return (
    <div style={{ minWidth: 0, display: "flex", alignItems: "center", gap: 12 }}>
      <StatusIcon mode={hm} t={THEME[hm]} />
      <span style={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <span style={{ fontWeight: 700, fontSize: 12, color: t.title }}>{title}</span>
        <span style={{ fontSize: 10, color: t.text }}>{subtitle}</span>
      </span>
    </div>
  );
}

function HalfGrid({ mode, platforms, failed }) {
  const hm = halfMode(mode, failed);
  const bar = THEME[hm].bar;
  const issues = mode !== "pushing" && failed > 0;
  // One CSS grid for the whole half, two platforms per row: name | count | bar
  // (or name | Success | Failed), a spacer column, then the second platform.
  // Every column is as wide as its widest cell, so the counts sit right after
  // the names, the bars start together down the column, and nothing drifts
  // apart on a narrow screen.
  const cols = "max-content max-content max-content 24px max-content max-content max-content";
  const cells = [];
  platforms.forEach((p, i) => {
    if (i % 2 === 1) cells.push(<span key={`gap-${p.platform}`} />);
    cells.push(<strong key={`n-${p.platform}`} style={{ color: BLACK }}>{p.platform}</strong>);
    if (issues) {
      cells.push(<span key={`s-${p.platform}`}><Dot color={GREEN} /><span style={{ color: GREEN }}>{num(p.success)}</span> Success</span>);
      cells.push(<span key={`f-${p.platform}`}>{p.failed > 0 && <><Dot color={RED} /><span style={{ color: RED }}>{num(p.failed)}</span> Failed</>}</span>);
    } else {
      cells.push(<span key={`c-${p.platform}`}><strong style={{ color: bar }}>{num(p.done)}</strong> / {num(p.total)}</span>);
      cells.push(<Bar key={`b-${p.platform}`} done={p.done} total={p.total} color={bar} />);
    }
  });
  return (
    <div style={{ minWidth: 0, display: "grid", gridTemplateColumns: cols, columnGap: 10, rowGap: 8, alignItems: "center", color: GREY, justifyContent: "start" }}>
      {cells}
    </div>
  );
}

export default function ListingSyncBanner() {
  const [batch, setBatch] = useState(null);
  const [retrying, setRetrying] = useState(false);
  const [reporting, setReporting] = useState(false);

  const load = useCallback(async () => {
    const res = await apiHelper("sales/listing/upload-batch/active/", "GET");
    if (!res || !res.ok) return null;
    const data = await res.json().catch(() => null);
    setBatch(data && data.id ? data : null);
    return data;
  }, []);

  useEffect(() => { load(); }, [load]);

  const batchId = batch ? batch.id : null;
  const pushing = !!batch && (batch.status === "saved" || batch.status === "pushing");

  // Poll only while this tab is in front (see PricingSyncBanner for why).
  useEffect(() => {
    if (!pushing) return undefined;
    let timer = null;
    const start = () => { if (timer === null) timer = setInterval(load, POLL_MS); };
    const stop = () => { if (timer !== null) { clearInterval(timer); timer = null; } };
    const onVisibility = () => {
      if (document.hidden) { stop(); return; }
      load();
      start();
    };
    if (!document.hidden) start();
    document.addEventListener("visibilitychange", onVisibility);
    return () => { stop(); document.removeEventListener("visibilitychange", onVisibility); };
  }, [batchId, pushing, load]);

  if (!batch) return null;

  const mappingFailed = batch.mapping_failed || 0;
  const qtyFailed = batch.qty_failed || 0;
  const hasIssues = mappingFailed > 0 || qtyFailed > 0;
  const mode = pushing ? "pushing" : hasIssues ? "bad" : "ok";
  const t = THEME[mode];
  const platforms = batch.platforms || { mapping: [], qty: [] };
  const canRetry = mode === "bad" && (batch.retries_left || 0) > 0;

  const dismiss = async () => {
    setBatch(null);
    await apiHelper(`sales/listing/upload-batch/${batch.id}/dismiss/`, "POST", {});
  };

  const downloadReport = async () => {
    if (reporting) return;
    setReporting(true);
    try { await downloadXlsx(`sales/listing/upload-batch/${batch.id}/report/`, "listing-import-report.xlsx"); }
    finally { setReporting(false); }
  };

  const retry = async () => {
    if (retrying || !canRetry) return;
    setRetrying(true);
    try {
      const res = await apiHelper(`sales/listing/upload-batch/${batch.id}/retry/`, "POST", {});
      if (res && res.ok) {
        const data = await res.json().catch(() => null);
        if (data && data.id) setBatch(data);
      }
    } finally {
      setRetrying(false);
    }
  };

  return (
    <div style={{ position: "relative", margin: "0 0 12px", padding: 15, borderRadius: 12, border: `1px solid ${t.border}`, background: t.bg, fontFamily: "Montserrat", fontSize: 12, color: t.text }}>
      {/* One grid for the whole banner so the title row and the platform row
          split at the same x: columns = left half | divider | right half |
          buttons. Row 1 titles + buttons, row 2 the full-width hairline, row 3
          the two platform grids with the divider between them (Figma). */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1px 1fr auto", columnGap: 24, alignItems: "start" }}>
        <HalfTitle mode={mode} t={t} title="Creating Listing Mapping" subtitle="Create or update the mapping between Platform Listing and ERP Stock" failed={mappingFailed} />
        <span />
        <HalfTitle mode={mode} t={t} title="Syncing Stock Qty to Platforms" subtitle="Update Platform Stock based on the latest mapping" failed={qtyFailed} />
        <div style={{ display: "flex", alignItems: "flex-start", gap: 12, paddingRight: 32 }}>  {/* 32px = the ✕ pinned to the banner corner below + its gap */}
          {!pushing && (
            <button type="button" onClick={downloadReport} disabled={reporting}
              style={{ display: "flex", alignItems: "center", gap: 6, height: 28, padding: "0 10px", borderRadius: 6, border: "1px solid #212529", background: "#fff", color: "#212529", fontFamily: "Montserrat", fontSize: 11, fontWeight: 600, cursor: reporting ? "progress" : "pointer" }}>
              <Download size={12} /> {reporting ? "Preparing…" : "Report"}
            </button>
          )}
          {canRetry && (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
              <button type="button" onClick={retry} disabled={retrying}
                style={{ display: "flex", alignItems: "center", gap: 6, height: 28, padding: "0 10px", borderRadius: 6, border: "1px solid #212529", background: "#fff", color: "#212529", fontFamily: "Montserrat", fontSize: 11, fontWeight: 600, cursor: retrying ? "progress" : "pointer" }}>
                <RefreshCw size={12} /> {retrying ? "Retrying…" : "Try Again"}
              </button>
              <span style={{ fontSize: 10, fontWeight: 600, color: GREY }}>{batch.retries_left} attempt{batch.retries_left === 1 ? "" : "s"} remaining</span>
            </div>
          )}
          {!pushing && (
            <button type="button" onClick={dismiss} aria-label="Dismiss" style={{ position: "absolute", top: 15, right: 15, background: "none", border: "none", cursor: "pointer", padding: 0, height: 28, display: "flex", alignItems: "center", color: BLACK }}><X size={20} strokeWidth={2.5} /></button>
          )}
        </div>

        <div style={{ gridColumn: "1 / -1", height: 1, background: LINE, margin: "10px 0" }} />

        <HalfGrid mode={mode} platforms={platforms.mapping || []} failed={mappingFailed} />
        <div style={{ width: 1, alignSelf: "stretch", background: LINE }} />   {/* divider between the two grids, below the hairline only */}
        <HalfGrid mode={mode} platforms={platforms.qty || []} failed={qtyFailed} />
        <span />
      </div>
      <style>{"@keyframes spin { to { transform: rotate(360deg); } }"}</style>
    </div>
  );
}
